import {
  useQuery,
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import service from '../service';
import type { PaginatedResponse } from '../types/api';
import type { RegistrationCode } from '../types/registrationCode';

type InviteCodesResponse = PaginatedResponse<RegistrationCode[]>;

type SearchParams = Record<string, unknown>;

export const inviteCodeQueryKeys = {
  all: ['inviteCodes'] as const,
  lists: () => [...inviteCodeQueryKeys.all, 'list'] as const,
  list: (params: SearchParams) =>
    [...inviteCodeQueryKeys.lists(), params] as const,
  details: () => [...inviteCodeQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...inviteCodeQueryKeys.details(), id] as const,
  usage: (id: string) => [...inviteCodeQueryKeys.detail(id), 'usage'] as const,
  subscriptions: (id: string, params: SearchParams) =>
    [...inviteCodeQueryKeys.detail(id), 'subscriptions', params] as const,
  preview: (serializedPayload: string | null) =>
    [...inviteCodeQueryKeys.all, 'preview', serializedPayload] as const,
};

const MAX_MUTATION_RETRIES = 2;

/** Retries invite code writes only on network failures and 5xx responses. */
export const retryInviteCodeMutation = (
  failureCount: number,
  error: unknown,
) => {
  const status = (error as { status?: number } | null)?.status ?? null;
  const isServerOrNetwork = status == null || status >= 500;
  return failureCount < MAX_MUTATION_RETRIES && isServerOrNetwork;
};

type UseInviteCodesParams = {
  searchParams?: SearchParams;
  enabled?: boolean;
};

export function useInviteCodes<TData = InviteCodesResponse, TError = unknown>(
  { searchParams = {}, enabled = true }: UseInviteCodesParams = {},
  options?: Omit<
    UseQueryOptions<InviteCodesResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  return useQuery<InviteCodesResponse, TError, TData, QueryKey>({
    ...options,
    queryKey: inviteCodeQueryKeys.list(searchParams),
    enabled,
    queryFn: async () => service.getAllRegistrationCodes(searchParams),
  });
}
